import { useCallback, useMemo, useState } from 'react';
import { hasXyz } from '../lib/dataset-points3d';
import { isScatterDataset } from '../lib/raw-scatter3d';
import useDatasetPoints from './useDatasetPoints';
import useRawScatter from './useRawScatter';

/**
 * Which x/y/z datasets are drawn as point clouds (opt-in per dataset: each holds up to 200k points)
 * and their fetched points / failures; the same opt-in for the apparent-resistivity scatter of raw
 * surveys (`scatterIds`, `scatter`). Shared by the /3d page's Layers list and the docked panel.
 * Pass `drawnIds` / `points` (and `scatterIds` / `scatter`) on to Viewer3DScene.
 */
export default function useViewer3dDatasets(datasets) {
  const [drawnIds, setDrawnIds] = useState([]);
  const drawnDatasets = useMemo(() => datasets.filter((d) => drawnIds.includes(d.id) && hasXyz(d)), [datasets, drawnIds]);
  const points = useDatasetPoints(drawnDatasets);
  const toggleDrawn = useCallback((id) => setDrawnIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])), []);

  const [scatterIds, setScatterIds] = useState([]);
  const scatterDatasets = useMemo(() => datasets.filter((d) => scatterIds.includes(d.id) && isScatterDataset(d)), [datasets, scatterIds]);
  const scatter = useRawScatter(scatterDatasets);
  const toggleScatter = useCallback((id) => setScatterIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])), []);

  // Saved view: set the ticks to the saved ids that still exist as drawable datasets (anything else is dropped)
  const restoreTicks = useCallback((ticks) => {
    setDrawnIds((ticks?.drawn || []).filter((id) => datasets.some((d) => d.id === id && hasXyz(d))));
    setScatterIds((ticks?.scatter || []).filter((id) => datasets.some((d) => d.id === id && isScatterDataset(d))));
  }, [datasets]);

  return { drawnIds, toggleDrawn, points, scatterIds, toggleScatter, scatter, restoreTicks };
}
